import { useEffect, useMemo, useState } from 'react'
import { useToast } from '../components/Toast'
import { loadReviewFlags, setReviewHandled } from '../lib/data'
import { naira, lagosTime } from '../lib/format'

// "Needs a look" — the things worth asking about, in one place.
//
// This page does NOT decide that anyone has done anything wrong, and it
// is written so nobody reads it that way. A deleted sale is nearly
// always a typo being fixed; a big write-off is nearly always a real
// breakage. What was missing was any way to SEE those things: they sat
// in an audit table nobody opens, which is how a bottle came back onto
// a shelf after a count with no trace, and how ₦48,500 of folio charges
// disappeared without anyone noticing until it was looked for.
//
// So: every row is a question with a name and a figure attached. The
// wording stays neutral on purpose — "worth asking about", never
// "suspicious" — because the page will be read by the same people it
// names, and an accusation generated by a rule is worse than no page.

const KIND = {
  backdated_change: {
    title: 'Changed after the day',
    why: 'An entry for an earlier day was edited or deleted. Normal when a mistake is being corrected — worth seeing when that day has already been counted and closed.',
  },
  money_change: {
    title: 'A payment was changed',
    why: 'Payments and repayments are money. Any edit or deletion deserves a second pair of eyes, whatever the reason.',
  },
  many_deletions: {
    title: 'Several deletions by one person',
    why: 'Any single deletion is fine. A run of them is worth a conversation — usually it means someone is struggling with the entry screen.',
  },
  blank_count: {
    title: 'Counted blank',
    why: 'A count line was submitted with nothing in it. Not counted is not the same as zero, and the balance keeps whatever it had.',
  },
  self_verified: {
    title: 'Counted and verified alone',
    why: 'The point of verifying is that a second person looks. Same name on both ends means nobody did.',
  },
  heavy_writeoff: {
    title: 'A lot of PR and damage',
    why: 'Free drinks and breakages are part of the business. A large total against one name is worth understanding, not assuming.',
  },
  stale_reopen: {
    title: 'Reopened and never re-dated',
    why: 'A stay was reopened to fix something and checked out again on the wrong day, so it is charging nights the guest did not stay.',
  },
  overpaid: {
    title: 'Paid more than the bill',
    why: 'Either money is owed back to the guest, or a payment was recorded twice.',
  },
  left_owing: {
    title: 'Left still owing',
    why: 'The guest has gone and the bill was not settled.',
  },
  late_entry: {
    title: 'Entered days late',
    why: 'A sale recorded well after its day was missing when that day was counted, which shows up later as a stock variance nobody can explain.',
  },
}

const SEV = {
  high:  { label: 'Look at this', ring: 'border-clay',  text: 'text-clay' },
  watch: { label: 'Worth asking', ring: 'border-amber', text: 'text-amber' },
  note:  { label: 'For the record', ring: 'border-line', text: 'text-dim' },
}

const dt = v => {
  if (!v) return ''
  const d = new Date(v)
  return `${d.toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })} ${lagosTime(v)}`
}

export default function Review({ boot }) {
  const [days, setDays] = useState(7)
  const [flags, setFlags] = useState(null)
  const [err, setErr] = useState(null)
  const [open, setOpen] = useState({})
  const [showHandled, setShowHandled] = useState(false)
  const [saving, setSaving] = useState(null)
  const toast = useToast()

  // Marking something handled stops it being counted tomorrow. It hides
  // no record: the entry it points at is untouched, and Put back undoes
  // it. Updated in place rather than reloading, so working down a list
  // does not reshuffle it under the reader's hands.
  async function mark(f, handled) {
    setSaving(f.key)
    try {
      await setReviewHandled(boot.staff.branch_id, f.key, handled)
      setFlags(list => list.map(x => x.key === f.key
        ? { ...x, handled, handled_by: handled ? boot.staff.full_name : null,
            handled_at: handled ? new Date().toISOString() : null,
            kind_open: (x.kind_open ?? 0) + (handled ? -1 : 1) }
        : x.kind === f.kind
          ? { ...x, kind_open: (x.kind_open ?? 0) + (handled ? -1 : 1) }
          : x))
    } catch (e) { toast(e.message || 'Could not save', 'error') }
    setSaving(null)
  }

  useEffect(() => {
    setFlags(null); setErr(null)
    loadReviewFlags(boot.staff.branch_id, days)
      .then(setFlags)
      .catch(e => { setErr(e.message || 'could not load'); setFlags([]) })
  }, [boot.staff.branch_id, days])

  const groups = useMemo(() => {
    const by = new Map()
    for (const f of flags || []) {
      if (!by.has(f.kind)) by.set(f.kind, [])
      by.get(f.kind).push(f)
    }
    // Most serious kind first, then most rows.
    const rank = g => Math.min(...g.map(f => f.severity === 'high' ? 0 : f.severity === 'watch' ? 1 : 2))
    return [...by.entries()].sort((a, b) => rank(a[1]) - rank(b[1]) || b[1].length - a[1].length)
  }, [flags])

  // Counted from the rows actually returned. Each kind is capped at 25
  // by the RPC, so a category with more says so on its own row rather
  // than inflating a headline nobody can act on.
  const counts = useMemo(() => {
    const c = { high: 0, watch: 0, note: 0 }
    for (const f of flags || []) if (!f.handled) c[f.severity] = (c[f.severity] || 0) + 1
    return c
  }, [flags])

  return (
    <div className="px-5 pb-10">
      <div className="py-3 border-b border-line mb-3">
        <h1 className="text-2xl font-bold">Needs a look</h1>
        <p className="text-dim text-sm mt-1">
          Things worth asking about at {boot.branchName}. Nothing here means anyone
          did wrong — most of it is ordinary correction. It is here so it is seen.
        </p>
        {/* Four buttons at px-4 in one unwrapping row ran off the side
            of a phone. The toggle keeps its width; the three periods are
            a group that drops to a second line together rather than one
            of them hanging off the edge alone. */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button onClick={() => setShowHandled(v => !v)}
            className={`h-10 px-4 rounded-xl border font-semibold shrink-0 ${showHandled
              ? 'border-leaf text-leaf' : 'border-line text-dim'}`}>
            {showHandled ? 'Hiding nothing' : 'Show handled'}
          </button>
          <div className="flex gap-2 shrink-0">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`h-10 px-3 rounded-xl border font-semibold text-sm shrink-0 ${days === d
                  ? 'bg-amber text-bg border-amber' : 'border-line text-dim'}`}>
                {d} days
              </button>
            ))}
          </div>
        </div>
      </div>

      {flags === null && <p className="text-dim py-8 text-center">Loading…</p>}
      {err && <p className="text-clay py-4">{err}</p>}

      {flags !== null && !flags.length && !err && (
        <p className="text-leaf py-8 text-center">
          Nothing to look at in the last {days} days.
        </p>
      )}

      {!!flags?.length && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 mb-4 text-sm">
          {counts.high > 0 && <span className="text-clay"><b className="text-lg tnum">{counts.high}</b> to look at</span>}
          {counts.watch > 0 && <span className="text-amber"><b className="text-lg tnum">{counts.watch}</b> worth asking</span>}
          {counts.note > 0 && <span className="text-dim"><b className="text-lg tnum">{counts.note}</b> for the record</span>}
        </div>
      )}

      {groups.map(([kind, rows]) => {
        const meta = KIND[kind] || { title: kind, why: '' }
        const live = rows.filter(r => !r.handled)
        const openCount = rows[0]?.kind_open ?? live.length
        const doneCount = (rows[0]?.kind_total ?? rows.length) - openCount
        const shown = showHandled ? rows : live
        const worst = live.some(r => r.severity === 'high') ? 'high'
          : live.some(r => r.severity === 'watch') ? 'watch' : 'note'
        const sev = SEV[worst]
        const isOpen = open[kind] ?? (worst === 'high')
        return (
          <section key={kind} className={`mt-3 rounded-2xl border ${sev.ring} bg-surface`}>
            <button onClick={() => setOpen(o => ({ ...o, [kind]: !isOpen }))}
              className="w-full text-left px-4 py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-bold">{meta.title}</p>
                <p className={`text-sm ${sev.text}`}>
                  {openCount} {openCount === 1 ? 'entry' : 'entries'} · {sev.label}
                  {doneCount > 0 && <span className="text-dim"> · {doneCount} handled</span>}
                </p>
              </div>
              <span className="text-dim">{isOpen ? '−' : '+'}</span>
            </button>

            {isOpen && (
              <div className="px-4 pb-4">
                <p className="text-dim text-sm mb-3">{meta.why}</p>
                <ul className="divide-y divide-line/60">
                  {shown.map((f, i) => (
                    <li key={`${f.key}-${i}`} className={`py-2 ${f.handled ? 'opacity-45' : ''}`}>
                      <div className="flex items-baseline gap-3">
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold">{f.who}</span>
                          {f.where && <span className="text-dim"> · {f.where}</span>}
                        </span>
                        {f.amount != null && (
                          <span className={`tnum font-semibold shrink-0 ${
                            f.handled ? 'text-dim' : SEV[f.severity].text}`}>
                            {naira(Number(f.amount))}
                          </span>
                        )}
                      </div>
                      <p className="text-dim text-sm">{f.summary}</p>
                      <div className="flex items-baseline gap-3 mt-0.5">
                        <span className="flex-1 min-w-0 text-dim text-xs tnum truncate">
                          {f.happened ? dt(f.happened) : ''}
                          {f.handled && f.handled_by ? ` · handled by ${f.handled_by}` : ''}
                        </span>
                        <button onClick={() => mark(f, !f.handled)} disabled={saving === f.key}
                          className={`shrink-0 h-8 px-3 rounded-lg border text-xs font-semibold
                            disabled:opacity-40 ${f.handled
                              ? 'border-line text-dim' : 'border-leaf text-leaf'}`}>
                          {saving === f.key ? '…' : f.handled ? 'Put back' : 'Mark handled'}
                        </button>
                      </div>
                    </li>
                  ))}
                  {!shown.length && (
                    <li className="py-6 text-center text-leaf text-sm">
                      All handled.
                    </li>
                  )}
                </ul>
                {rows[0]?.kind_total > rows.length && (
                  <p className="text-dim text-sm mt-3">
                    Showing the {rows.length} most recent of {rows[0].kind_total}.
                    Narrow the days above to see fewer.
                  </p>
                )}
              </div>
            )}
          </section>
        )
      })}

      {!!flags?.length && (
        <p className="text-dim text-xs mt-6">
          Ask before concluding. Every one of these has an innocent explanation
          that is usually the right one.
        </p>
      )}
    </div>
  )
}
